import { getToken } from "utils/helper";
import {
  CustomApiResponse,
  PaginatedApiResponse,
} from "utils/helper/ApiResponses";
import {
  CountryInfo,
  GetProductsQuery,
  PricingRules,
  Product,
} from "utils/types";
import { apiSrcUrl } from "../ApiRoutes";
import { ApiService } from "../apiservices";
import { Provider } from "utils/types/Provider.types";


export const getTopSellingProducts = async (
  limit = 5
): Promise<Product[]> => {
  const url = `${apiSrcUrl}/public/product/topsells?limit=${limit}`;
  const { data } = await ApiService.get<CustomApiResponse<Product[]>>(url);
  if (data.status !== "success" || !data.data) {
    throw new Error(data.message || "Failed to fetch top-selling products.");
  }
  return data.data;
};

/**
 * Fetches a paginated and filtered list of all products.
 * @param params - The query parameters for filtering, sorting, and pagination.
 * @returns A promise that resolves to the paginated list of products.
 */
export const getProducts = async (
  params: GetProductsQuery
): Promise<PaginatedApiResponse<Product>> => {
  const query = new URLSearchParams();

  // Append query parameters if they exist
  if (params.page) query.append("page", params.page.toString());
  if (params.limit) query.append("limit", params.limit.toString());
  if (params.search) query.append("search", params.search);
  if (params.sortBy) query.append("sortBy", params.sortBy);
  if (params.order) query.append("order", params.order);
  if (params.category) query.append("category", params.category);
  if (params.provider) query.append("provider", params.provider);
  if (params.country) query.append("country", params.country);
  if (params.isBestseller !== undefined)
    query.append("isBestseller", String(params.isBestseller));
  if (params.groupBy) query.append("groupBy", params.groupBy);

  // FIX: Use the new pluralized endpoint
  const url = `${apiSrcUrl}/public/product?${query.toString()}`;
  const response = await ApiService.get<
    CustomApiResponse<PaginatedApiResponse<Product>>
  >(url);

  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Failed to fetch products.");
  }
  return response.data.data;
};

/**
 * Fetches a single product by its ID.
 * @param productId - The ID of the product to fetch.
 * @returns A promise that resolves to the product data.
 */
export const getProductById = async (productId: string): Promise<Product> => {
  const url = `${apiSrcUrl}/public/product/prod/${productId}`;
  const response = await ApiService.get<CustomApiResponse<Product>>(url);

  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Product not found.");
  }
  return response.data.data;
};

/**
 * Creates a new product. Admin only.
 * @param productData - The data for the new product.
 * @returns The newly created product record.
 */
export const createProduct = async (
  productData: Omit<Product, "id">
): Promise<Product> => {
  const token = getToken();
  const url = `${apiSrcUrl}/private/product`;
  const response = await ApiService.post<CustomApiResponse<Product>>(
    url,
    productData,
    {
      Authorization: `Bearer ${token}`,
    }
  );
  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Failed to create product.");
  }
  return response.data.data;
};

/**
 * Updates an existing product. Admin only.
 * @param id - The ID of the product to update.
 * @param productData - The fields to be updated.
 * @returns The updated product record.
 */
export const updateProduct = async (
  id: string,
  productData: Partial<Product>
): Promise<Product> => {
  const token = getToken();
  const url = `${apiSrcUrl}/private/product/${id}`;

  console.log("Updating product with data:", productData);
  console.log("URL:", url);
  const { priceUSD, pricing, ...updatable } = productData; // strip here too
  const response = await ApiService.put<CustomApiResponse<Product>>(
    url,
    updatable,
    {
      Authorization: `Bearer ${token}`,
    }
  );
  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Failed to update product.");
  }
  return response.data.data;
};

/**
 * Updates only the pricing rules for a specific product. Admin only.
 * @param id - The ID of the product to update.
 * @param pricingData - The new pricing rules object.
 * @returns The updated product record.
 */
export const updateProductPricing = async (
  id: string,
  pricingData: PricingRules
): Promise<Product> => {
  const token = getToken();
  const url = `${apiSrcUrl}/private/product/${id}`;
  const response = await ApiService.patch<CustomApiResponse<Product>>(
    url,
    pricingData,
    {
      Authorization: `Bearer ${token}`,
    }
  );
  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(
      response.data.message || "Failed to update product pricing."
    );
  }
  return response.data.data;
};

/**
 * Deletes a product. Admin only.
 * @param id - The ID of the product to delete.
 */
export const deleteProduct = async (id: string): Promise<void> => {
  const token = getToken();
  const url = `${apiSrcUrl}/private/product/${id}`;
  const response = await ApiService.delete<CustomApiResponse<any>>(url, {
    Authorization: `Bearer ${token}`,
  });
  if (response.data.status !== "success") {
    throw new Error(response.data.message || "Failed to delete product.");
  }
};

/**
 * Fetches a list of all available providers.
 * This is a public endpoint and does not require authentication.
 * @returns A promise that resolves to an array of providers.
 */
export const getAllProviders = async (): Promise<Provider[]> => {
  const url = `${apiSrcUrl}/public/providers`;

  const response = await ApiService.get<CustomApiResponse<Provider[]>>(url);

  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Failed to fetch providers.");
  }

  return response.data.data;
};

/**
 * Creates a new provider. Admin only.
 * @param providerData - The data for the new provider.
 * @returns The newly created provider record.
 */
export const createProvider = async (
  providerData: Partial<Omit<Provider, "id">>
): Promise<Provider> => {
  const token = getToken();
  const url = `${apiSrcUrl}/private/providers`;
  console.log("Creating provider with data:", providerData);
  console.log("URL:", url);

  const response = await ApiService.post<CustomApiResponse<Provider>>(
    url,
    providerData,
    {
      Authorization: `Bearer ${token}`,
    }
  );

  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Failed to create provider.");
  }

  return response.data.data;
};

/**
 * Fetches a single provider's details and a list of associated countries.
 * @param providerName - The name of the provider to fetch.
 * @returns An object containing provider details and a list of countries.
 */
export const getProviderByName = async (
  providerName: string
): Promise<{
  provider: Provider;
  countries: CountryInfo[];
  defaultCountry: CountryInfo | null;
}> => {
  const url = `${apiSrcUrl}/public/providers/${providerName}`;
  const response = await ApiService.get<
    CustomApiResponse<{
      provider: Provider;
      countries: CountryInfo[];
      defaultCountry?: CountryInfo | null | null;
    }>
  >(url);

  if (response.data.status !== "success" || !response.data.data) {
    throw new Error(response.data.message || "Provider not found.");
  }

  const { provider, countries: rawCountries } = response.data.data;

  const countries: CountryInfo[] = Array.isArray(rawCountries)
    ? rawCountries.map((item) => ({ code: item.code, name: item.name }))
    : [];

  return { provider, countries, defaultCountry: countries[0] ?? null };
};
